import { Box, Group, Text, Button, ActionIcon } from '@mantine/core'
import { IconArrowRight, IconArrowLeft, IconSend, IconCheck } from '@tabler/icons-react'
import { ChoiceButtons } from '../game/ChoiceButtons'
import { InputPanel } from '../game/InputPanel'
import { getBeatTypeColor } from '../../lib/beatColors'
import { SectionLabel } from './SectionLabel'

// Pinned bottom of the Story Column. Morphs by beat type: stacked choices for a
// decision, a text box for input, Continue/Complete for everything else. Back
// lives in the footer. Locked beats (revisited, already answered) skip the
// prompt and just advance, mirroring the legacy DialoguePanel logic.
export function ResponseDock({
  currentBeat,
  selectedChoice,
  inputValue,
  isLoading,
  canGoNext,
  onNext,
  onChoiceSelect,
  onInputSubmit,
  onInputChange,
  canGoPrev,
  onPrev,
  locked = false,
}) {
  const isDecision = currentBeat?.beat_type === 'decision'
  const isInput = currentBeat?.beat_type === 'input'
  const beatColor = getBeatTypeColor(currentBeat?.beat_type)
  const isLastBeat = !canGoNext

  // The control collapses to just the advance button on narrative/generated
  // beats — anything that isn't an input beat or a decision with choices. The
  // reserved static height is only worth holding when the control actually
  // varies in size (choices, text field); a lone Continue button can hug.
  const hasChoices = isDecision && currentBeat.options?.choices?.length > 0
  const isAdvanceOnly = !isInput && !hasChoices

  // Continue/Complete advance button, shared by narrative/generated beats, by
  // revisited (locked) decision/input beats, and by an unlocked decision once a
  // choice is picked — so selecting an option commits and moves forward.
  const advanceButton = (extraDisabled = false) => (
    <Group justify="center">
      <Button
        color={beatColor}
        variant="light"
        size="md"
        rightSection={isLastBeat ? <IconCheck size={16} /> : <IconArrowRight size={16} />}
        onClick={onNext}
        disabled={isLoading || extraDisabled}
      >
        {isLastBeat ? 'Complete' : 'Continue'}
      </Button>
    </Group>
  )

  const renderControl = () => {
    // Decision: always show the choices (locked or not) so revisiting a past
    // decision shows the options with the committed pick highlighted, instead of
    // collapsing to a bare Continue button. ChoiceButtons renders the chosen
    // option lit with a lock affordance and the rest faded/non-interactive when
    // locked. A Continue button advances: always present on locked beats; on an
    // active decision it's disabled until a choice is selected (selecting only
    // sets the pick — Continue commits and moves on, mirroring the legacy panel).
    if (hasChoices) {
      return (
        <Box>
          <ChoiceButtons
            choices={[...currentBeat.options.choices].sort((a, b) => a.position - b.position)}
            selectedChoice={selectedChoice}
            onSelect={onChoiceSelect}
            locked={locked}
          />
          {!locked && !selectedChoice && (
            <Text size="sm" c="dimmed" fs="italic" ta="center" mt="xs" ff="var(--bw-body-font)">
              Select a choice to continue
            </Text>
          )}
          <Box mt="sm">{advanceButton(!locked && !selectedChoice)}</Box>
        </Box>
      )
    }

    if (isInput) {
      // Stacked layout: a large response field on top, the action below it.
      // Active beat → editable field + a full-width Submit (the clear primary
      // action). Revisited (locked) beat → read-only field + Continue.
      return (
        <Box>
          <InputPanel
            value={inputValue}
            onChange={onInputChange}
            onSubmit={onInputSubmit}
            locked={locked}
            fill={false}
          />
          <Box mt="sm">
            {locked ? (
              advanceButton()
            ) : (
              <Button
                fullWidth
                color={beatColor}
                variant="light"
                size="md"
                rightSection={<IconSend size={16} />}
                onClick={onInputSubmit}
                disabled={!inputValue?.trim()}
              >
                Submit
              </Button>
            )}
          </Box>
        </Box>
      )
    }

    // narrative / generated → advance
    return advanceButton()
  }

  return (
    <Box className="g2-dock" px="lg" pt="sm" pb="md" style={{ borderTop: '1px solid var(--bw-panel-border)' }}>
      <SectionLabel>Response</SectionLabel>
      <Box className={`g2-dock-control${isInput ? ' is-input' : ''}${isAdvanceOnly ? ' is-advance' : ''}`}>{renderControl()}</Box>
      <Group justify="flex-start" mt="xs">
        {canGoPrev && (
          <ActionIcon
            variant="subtle"
            color="gray"
            size="md"
            onClick={onPrev}
            aria-label="Back"
            style={{ opacity: 0.6 }}
          >
            <IconArrowLeft size={18} />
          </ActionIcon>
        )}
      </Group>
    </Box>
  )
}
